import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import axios from 'axios';
import { configs } from '../../config';
import './IngestedDataInsights.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const IngestedDataInsights = ({ startDate, endDate }) => {
  const [brandData, setBrandData] = useState([]);
  const [allBrandData, setAllBrandData] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllData, setShowAllData] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'totalMentions', direction: 'desc' });
  const [metricsLoading, setMetricsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
    // Only refetch metrics when toggle changes and we have brand data
    if (allBrandData.length > 0 && brandData.length > 0) {
      const brandsToUse = showAllData ? allBrandData : brandData;
      console.log(`Toggle changed - Fetching metrics for ${showAllData ? 'ALL' : 'FILTERED'} brands:`, brandsToUse.length);
      fetchMetricsForBrands(brandsToUse);
    }
  }, [showAllData]); // Only depend on showAllData for toggle


  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch brand wise details
      const brandResponse = await axios.get(
        `${configs.cmoweb_url}/api/Dashboard/Dashboard/BrandWiseDetails`,
        {
          headers: {
            'X-API-Key': configs.api_key
          }
        }
      );

      if (brandResponse.data && brandResponse.data.data) {
        const brands = brandResponse.data.data;
        
        // Store all brands
        setAllBrandData(brands);
        
        // Filter brands by date
        const filteredBrands = brands.filter(brand => {
          if (brand.insertedDate) {
            const brandDate = new Date(brand.insertedDate);
            return brandDate >= new Date(startDate) && brandDate <= new Date(endDate);
          }
          return true;
        });
        setBrandData(filteredBrands);

        // Fetch metrics for the initial view (filtered brands)
        await fetchMetricsForBrands(filteredBrands);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data insights');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsForBrands = async (brands) => {
    try {
      setMetricsLoading(true);
      const brandIds = brands.map(brand => brand.brandId).join(',');
      
      if (brandIds) {
        const metricsResponse = await axios.get(
          `${configs.cmoweb_url}/api/Dashboard/Dashboard/BrandWiseMetricsFromClickhouse?BrandIdsString=${brandIds}`,
          {
            headers: {
              'X-API-Key': configs.api_key
            }
          }
        );

        if (metricsResponse.data && metricsResponse.data.data) {
          setMetricsData(metricsResponse.data.data);
        }
      } else {
        setMetricsData([]);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setMetricsData([]);
    } finally {
      setMetricsLoading(false);
    }
  };


  // Process data for sector mentions bar chart
  const getSectorMentionsChartData = () => {
    const sectorMentions = {};
    const dataToUse = showAllData ? allBrandData : brandData;
    
    // Combine brand data with metrics data
    dataToUse.forEach(brand => {
      const metric = metricsData.find(m => m.brandId === brand.brandId);
      if (metric) {
        const sector = brand.sectorName || 'Unknown';
        sectorMentions[sector] = (sectorMentions[sector] || 0) + (metric.totalMentionsFromBeginning || 0);
      }
    });

    const sortedSectors = Object.entries(sectorMentions)
      .sort(([, a], [, b]) => b - a); // Show all sectors

    return {
      labels: sortedSectors.map(([sector]) => sector),
      datasets: [{
        label: 'Total Mentions',
        data: sortedSectors.map(([, mentions]) => mentions),
        backgroundColor: '#36A2EB',
        borderColor: '#2496ED',
        borderWidth: 1
      }]
    };
  };


  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get the correct brand list based on toggle
  const brandsToShow = showAllData ? allBrandData : brandData;
  const brandIdsToShow = new Set(brandsToShow.map(b => b.brandId));
  
  // Filter metrics to only show those that match current toggle state
  const filteredMetricsData = metricsData.filter(metric => brandIdsToShow.has(metric.brandId));
  
  // Sort filtered metrics data
  const sortedMetricsData = [...filteredMetricsData].sort((a, b) => {
    // Always look in allBrandData for brand info since metrics might be from either filtered or all
    const brandA = allBrandData.find(brand => brand.brandId === a.brandId);
    const brandB = allBrandData.find(brand => brand.brandId === b.brandId);
    
    let aValue, bValue;
    
    switch(sortConfig.key) {
      case 'brand':
        aValue = brandA?.brandFriendlyName || brandA?.brandName || '';
        bValue = brandB?.brandFriendlyName || brandB?.brandName || '';
        break;
      case 'sector':
        aValue = brandA?.sectorName || '';
        bValue = brandB?.sectorName || '';
        break;
      case 'totalMentions':
        aValue = a.totalMentionsFromBeginning || 0;
        bValue = b.totalMentionsFromBeginning || 0;
        break;
      case 'trend':
        aValue = a.trendPercentage || 0;
        bValue = b.trendPercentage || 0;
        break;
      case 'brandPosts':
        aValue = a.brandPostsFromBeginning || 0;
        bValue = b.brandPostsFromBeginning || 0;
        break;
      case 'userComments':
        aValue = a.userCommentsFromBeginning || 0;
        bValue = b.userCommentsFromBeginning || 0;
        break;
      default:
        return 0;
    }
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      }
    },
    plugins: {
      legend: {
        display: false
      },
      datalabels: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const value = context.parsed.y;
              if (value >= 1000000) {
                label += (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                label += (value / 1000).toFixed(1) + 'K';
              } else {
                label += value.toLocaleString();
              }
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 90,
          minRotation: 45,
          autoSkip: false,
          font: {
            size: 11
          },
          padding: 5
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'K';
            }
            return value;
          },
          padding: 10
        },
        grid: {
          color: 'rgba(48, 54, 61, 0.5)'
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="ingested-data-insights">
        <h2>Ingested Data Insights</h2>
        <div className="loading-state">Loading insights...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ingested-data-insights">
        <h2>Ingested Data Insights</h2>
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="ingested-data-insights">
      <div className="insights-header">
        <h2>Ingested Data Insights</h2>
        <button 
          className="toggle-data-button"
          onClick={() => setShowAllData(!showAllData)}
        >
          {showAllData ? 'Show Filtered Data' : 'Show All Data'}
        </button>
      </div>
      
      <div className="data-info">
        {showAllData ? (
          <p>Showing all {allBrandData.length} brands</p>
        ) : (
          <p>Showing {brandData.length} brands from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}</p>
        )}
      </div>
      
      <div className="insights-grid">
        <div className="chart-container full-width">
          <h3>Top Sectors by Total Mentions</h3>
          <div className="chart-wrapper bar-chart">
            {metricsLoading ? (
              <div className="loading-state">Updating chart...</div>
            ) : (
              <Bar data={getSectorMentionsChartData()} options={barOptions} />
            )}
          </div>
        </div>
      </div>

      {/* Brand Metrics Summary */}
      <div className="brand-metrics-summary">
        <h3>Brand Performance Metrics ({metricsLoading ? '...' : filteredMetricsData.length} brands)</h3>
        <div className="metrics-table">
          {metricsLoading ? (
            <div className="loading-state" style={{ padding: '40px' }}>Updating table...</div>
          ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('brand')} className="sortable">
                  Brand {sortConfig.key === 'brand' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('sector')} className="sortable">
                  Sector {sortConfig.key === 'sector' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('totalMentions')} className="sortable">
                  Total Mentions {sortConfig.key === 'totalMentions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('trend')} className="sortable">
                  Trend % {sortConfig.key === 'trend' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('brandPosts')} className="sortable">
                  Brand Posts {sortConfig.key === 'brandPosts' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('userComments')} className="sortable">
                  User Comments {sortConfig.key === 'userComments' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedMetricsData.map(metric => {
                const brand = allBrandData.find(b => b.brandId === metric.brandId);
                return (
                  <tr key={metric.brandId}>
                    <td>{brand?.brandFriendlyName || brand?.brandName || `Brand ${metric.brandId}`}</td>
                    <td>{brand?.sectorName || 'Unknown'}</td>
                    <td>{metric.totalMentionsFromBeginning?.toLocaleString()}</td>
                    <td className={metric.trendPercentage >= 0 ? 'positive' : 'negative'}>
                      {metric.trendPercentage?.toFixed(1)}%
                    </td>
                    <td>{metric.brandPostsFromBeginning?.toLocaleString()}</td>
                    <td>{metric.userCommentsFromBeginning?.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default IngestedDataInsights;